import { Button, Modal, ModalHeader, ModalBody, ModalFooter, Select, TextInput } from "flowbite-react";
import type { FC } from "react";
import { useEffect, useState } from "react";
import { FaPlus } from "react-icons/fa";
import type { ContractCompany } from "../../services/contract-companies";
import { getContractCompanies } from "../../services/contract-companies";
import type { ContractCategory } from "../../services/contract-categories";
import { getContractCategories } from "../../services/contract-categories";
import { createContractType } from "../../services/contract-types";

interface AddContractTypeModalProps {
  onUpdate: () => Promise<void>;
}

export const AddContractTypeModal: FC<AddContractTypeModalProps> = function ({
  onUpdate,
}) {
  const [isOpen, setOpen] = useState(false);
  const [isActive, setIsActive] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [companies, setCompanies] = useState<ContractCompany[]>([]);
  const [categories, setCategories] = useState<ContractCategory[]>([]);
  const [formData, setFormData] = useState({
    type: "",
    company: "",
    category: "",
    description: "",
    minDuration: "24", // Default to 24 months
  });

  useEffect(() => {
    const loadData = async () => {
      console.log("Loading companies and categories...");
      const [companiesData, categoriesData] = await Promise.all([
        getContractCompanies(),
        getContractCategories(),
      ]);
      console.log("Companies loaded:", companiesData);
      console.log("Categories loaded:", categoriesData);
      setCompanies(companiesData);
      setCategories(categoriesData);
    };
    if (isOpen) {
      loadData();
    }
  }, [isOpen]);

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    if (!formData.type || !formData.company || !formData.category) {
      console.error("Missing required fields");
      return;
    }

    setIsSubmitting(true);
    try {
      const result = await createContractType({
        type: formData.type,
        company: formData.company,
        category: formData.category,
        description: formData.description,
        min_duration_months: parseInt(formData.minDuration),
        active: isActive,
      });

      if (result.success) {
        await onUpdate();
        setOpen(false);
        setFormData({
          type: "",
          company: "",
          category: "",
          description: "",
          minDuration: "24", // Reset to default
        });
      } else {
        console.error("Failed to create contract type:", result.error);
      }
    } catch (error) {
      console.error("Error creating contract type:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Button color="primary" onClick={() => setOpen(!isOpen)}>
        <FaPlus className="mr-3 text-sm" />
        Vertragstyp hinzufügen
      </Button>
      <Modal onClose={() => setOpen(false)} show={isOpen}>
        <ModalHeader className="border-b border-gray-200 !p-6 dark:border-gray-700">
          <strong>Vertragstyp hinzufügen</strong>
        </ModalHeader>
        <ModalBody>
          <form>
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
              <div>
                <label htmlFor="type">Typname</label>
                <TextInput
                  id="type"
                  name="type"
                  value={formData.type}
                  onChange={handleInputChange}
                  placeholder="Typnamen eingeben"
                  className="mt-1"
                />
              </div>
              <div>
                <label htmlFor="company">Unternehmen</label>
                <Select
                  id="company"
                  name="company"
                  value={formData.company}
                  onChange={handleInputChange}
                  className="mt-1"
                >
                  <option value="">Unternehmen auswählen</option>
                  {companies.map((company) => (
                    <option key={company.id} value={company.company}>
                      {company.company}
                    </option>
                  ))}
                </Select>
              </div>
              <div>
                <label htmlFor="category">Kategorie</label>
                <Select
                  id="category"
                  name="category"
                  value={formData.category}
                  onChange={handleInputChange}
                  className="mt-1"
                >
                  <option value="">Kategorie auswählen</option>
                  {categories.map((category) => (
                    <option key={category.id} value={category.category}>
                      {category.category}
                    </option>
                  ))}
                </Select>
              </div>
              <div>
                <label htmlFor="minDuration">Mindestlaufzeit (Monate)</label>
                <TextInput
                  id="minDuration"
                  name="minDuration"
                  type="number"
                  value={formData.minDuration}
                  onChange={handleInputChange}
                  placeholder="Mindestlaufzeit eingeben"
                  className="mt-1"
                />
              </div>
              <div className="lg:col-span-2">
                <label htmlFor="description">Beschreibung</label>
                <textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="Vertragstypbeschreibung eingeben"
                  rows={4}
                  className="mt-1 block w-full rounded-lg border border-gray-300 bg-gray-50 p-2.5 text-sm text-gray-900 focus:border-blue-500 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:placeholder:text-gray-400 dark:focus:border-blue-500 dark:focus:ring-blue-500"
                />
              </div>
              <div className="lg:col-span-2">
                <div className="flex items-center gap-2">
                  <input
                    id="active"
                    type="checkbox"
                    checked={isActive}
                    onChange={() => setIsActive(!isActive)}
                    className="h-4 w-4 rounded border-gray-300 bg-gray-100 text-blue-600 focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:ring-offset-gray-800 dark:focus:ring-blue-600"
                  />
                  <label htmlFor="active">Aktiver Status</label>
                </div>
              </div>
            </div>
          </form>
        </ModalBody>
        <ModalFooter>
          <div className="flex gap-2">
            <Button
              color="primary"
              onClick={handleSubmit}
              disabled={isSubmitting}
            >
              {isSubmitting ? "Wird erstellt..." : "Vertragstyp hinzufügen"}
            </Button>
            <Button color="gray" onClick={() => setOpen(false)}>
              Abbrechen
            </Button>
          </div>
        </ModalFooter>
      </Modal>
    </>
  );
};
